import {
  EXCHANGE_DIMENSIONS,
  REPORT_CUTS,
  REPORT_IDS,
  type ExchangeDimension,
  type ReportId,
} from '@aeris/contracts'

import { isPeriodChoice, type PeriodChoice } from './period-range.ts'
import {
  SALES_REPORT_MODES,
  type SalesReportMode,
} from './sales-period-report.ts'

/**
 * O que a URL de `/relatorios` aceita — F158.
 *
 * ## Por que esta tela guarda filtro na URL, e as outras não
 *
 * É a primeira do produto a fazer isso, e é exceção com motivo: um relatório se
 * manda por WhatsApp como link, sobrevive ao recarregar, e a janela do Modo PC
 * (F138) o restaura. Uma lista de vendas não tem esse uso — ninguém manda "o
 * filtro da minha tela de vendas" para alguém.
 *
 * ## A disciplina, que é a mesma das outras três rotas com `validateSearch`
 *
 * O que vem na URL é **entrada do usuário como qualquer outra**. Valor
 * desconhecido, vazio, repetido ou de outro tipo vira **ausência**, e a tela
 * abre no padrão — nunca um erro, nunca uma tela em branco.
 *
 * E nada daqui vira caminho nem chega ao SQL: cada campo é chave de um enum que
 * o contrato declara, e o servidor os valida **de novo** quando a requisição
 * chega. Esta função é conveniência para quem navega, não a fronteira de
 * segurança.
 *
 * Mora em `lib/`, e não ao lado do componente, pela mesma razão de
 * `lib/config-tabs.ts`: um módulo que mistura componente com outras exportações
 * faz o Fast Refresh desistir do arquivo inteiro, em silêncio.
 */

/**
 * O que o seletor de relatório oferece: os rankings do envelope comum e a
 * lista de vendas do período (F271), que tem forma própria.
 */
export const SALES_REPORT = 'vendas'
export type ReportChoice = ReportId | typeof SALES_REPORT

export type ReportSearch = {
  relatorio?: ReportChoice
  periodo?: PeriodChoice
  /** `AAAA-MM-DD`, e só valem quando `periodo` é `custom`. */
  de?: string
  ate?: string
  por?: ExchangeDimension
  corte?: number
  filial?: string
  /** F271: só as compras deste cliente. */
  cliente?: string
  /** F271: venda por venda, ou o total por dia. */
  modo?: SalesReportMode
}

const known = <T>(value: unknown, allowed: readonly T[]): T | undefined =>
  (allowed as readonly unknown[]).includes(value) ? (value as T) : undefined

const day = (value: unknown): string | undefined =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value
    : undefined

export const validateReportSearch = (
  search: Record<string, unknown>
): ReportSearch => {
  const relatorio = known<ReportChoice>(search.relatorio, [
    ...REPORT_IDS,
    SALES_REPORT,
  ])
  const modo = known(search.modo, SALES_REPORT_MODES)
  const por = known(search.por, EXCHANGE_DIMENSIONS)
  const corte = known(Number(search.corte), REPORT_CUTS)
  const de = day(search.de)
  const ate = day(search.ate)

  return {
    ...(relatorio === undefined ? {} : { relatorio }),
    ...(isPeriodChoice(search.periodo) ? { periodo: search.periodo } : {}),
    ...(de === undefined ? {} : { de }),
    ...(ate === undefined ? {} : { ate }),
    ...(por === undefined ? {} : { por }),
    ...(corte === undefined ? {} : { corte }),
    ...(typeof search.filial === 'string' && search.filial !== ''
      ? { filial: search.filial }
      : {}),
    ...(typeof search.cliente === 'string' &&
    /^[0-9a-f-]{36}$/i.test(search.cliente)
      ? { cliente: search.cliente }
      : {}),
    ...(modo === undefined ? {} : { modo }),
  }
}
